/**
 * Image acquisition
 *
 * File upload and `getUserMedia` capture. EXIF orientation is applied to the
 * pixels and then every tag — a palm photo taken at home carries GPS — is gone,
 * because a canvas re-encodes pixels only. `stripMetadata()` makes that explicit
 * for anything that must be saved or shared.
 *
 * The image never leaves the device. There is no upload endpoint to leave to.
 *
 * Browser-only (uses DOM and canvas). The pipeline itself takes plain RGBA and
 * has no such dependency.
 *
 * © Manish Jagdish Thatte
 */

export const WORKING_EDGE = 640;

/** Open the browser's own file picker and resolve with the chosen File, or null. */
export function pickFile({ accept = 'image/*' } = {}) {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = accept;
    // Deliberately no `capture` attribute: the browser's picker decides how to source the image.
    input.addEventListener('change', () => resolve(input.files?.[0] ?? null), { once: true });
    input.addEventListener('cancel', () => resolve(null), { once: true });
    input.click();
  });
}

/** Start a rear-camera stream into `videoEl`. Resolves with the MediaStream. Throws a plain Error if refused. */
export async function startCamera(videoEl, constraints = { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } }, audio: false }) {
  if (!navigator.mediaDevices?.getUserMedia) throw new Error('camera-unavailable');
  const stream = await navigator.mediaDevices.getUserMedia(constraints);
  videoEl.srcObject = stream; videoEl.playsInline = true; videoEl.muted = true;
  await videoEl.play();
  return stream;
}

export function stopCamera(stream) { stream?.getTracks().forEach((tr) => tr.stop()); }

/** Draw the current video frame to a canvas and return it. */
export function grabFrame(videoEl) {
  const c = document.createElement('canvas');
  c.width = videoEl.videoWidth; c.height = videoEl.videoHeight;
  c.getContext('2d').drawImage(videoEl, 0, 0);
  return c;
}

/**
 * Decode a File / Blob / canvas / video into working-size RGBA `ImageData`-shaped data.
 * `imageOrientation: 'from-image'` applies the EXIF rotation to the pixels first.
 * Returns `{ width, height, data, canvas }` where `canvas` is the drawn copy (for display).
 */
export async function toImageData(source, maxEdge = WORKING_EDGE) {
  let bitmap = source; let owned = false;
  if (source instanceof Blob) {
    bitmap = typeof createImageBitmap === 'function'
      ? await createImageBitmap(source, { imageOrientation: 'from-image' })
      : await new Promise((resolve, reject) => {
        const url = URL.createObjectURL(source); const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('unreadable-image')); };
        img.src = url;
      });
    owned = true;
  }
  const w = bitmap.videoWidth || bitmap.width; const h = bitmap.videoHeight || bitmap.height;
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w * scale)); canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  if (owned) bitmap.close?.();
  const id = ctx.getImageData(0, 0, canvas.width, canvas.height);
  return { width: id.width, height: id.height, data: id.data, canvas };
}

/** Re-encode an image as a PNG blob from pixels only — no EXIF, no GPS, no colour-profile tags. */
export function stripMetadata(canvasOrBlob) {
  const toBlob = (canvas) => new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode-failed'))), 'image/png'));
  if (canvasOrBlob instanceof HTMLCanvasElement) return toBlob(canvasOrBlob);
  return toImageData(canvasOrBlob, 4096).then((d) => toBlob(d.canvas));
}
